import { QUESTIONNAIRE_URL } from "env-vars";

export const getProtocolHost = () =>
  `${window.location.protocol}//${window.location.host}`;

export const preserveQueryParams = (path: string) =>
  `${path}${location.search}`;

// Deeplink paths that unauthenticated users can be redirected to after login
const ALLOWED_DEEPLINK_PATHS = ["/complete", "/complete/take-action"];

export const isAllowedDeeplinkPath = (path: string): boolean => {
  const pathname = path.split("?")[0];
  return ALLOWED_DEEPLINK_PATHS.includes(pathname);
};

const AUTH_RETURN_PATH_KEY = "auth_return_path";

export const setAuthReturnPath = (path: string): void => {
  if (isAllowedDeeplinkPath(path)) {
    sessionStorage.setItem(AUTH_RETURN_PATH_KEY, path);
  }
};

export const getAuthReturnPath = (): string | null => {
  const path = sessionStorage.getItem(AUTH_RETURN_PATH_KEY);
  if (path && isAllowedDeeplinkPath(path)) {
    sessionStorage.removeItem(AUTH_RETURN_PATH_KEY);
    return path;
  }
  return null;
};

export const getLoginRedirectUrl = (pathname: string, search: string): string => {
  if (isAllowedDeeplinkPath(pathname)) {
    setAuthReturnPath(pathname + search);
  }
  return `${preserveQueryParams("/login/passwordless")}`;
};

export const formatPhone = (value: string): string | null => {
  if (!value) return null;

  const cleaned = value.replace(/\D/g, ""); // removes all non digits
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }

  return cleaned || null;
};

type QueryStringParams = {
  uri: string;
  key: string;
  value: string;
};

const updateQueryStringParameter = ({ uri, key, value }: QueryStringParams) => {
  const re = new RegExp("([?&])" + key + "=.*?(&|$)", "i");
  const separator = uri.indexOf("?") !== -1 ? "&" : "?";
  if (uri.match(re)) {
    return uri.replace(re, "$1" + key + "=" + value + "$2");
  } else {
    return uri + separator + key + "=" + value;
  }
};

type OpenSurvey = {
  token: string;
  queryParams: object;
  newTab: boolean | null;
  skipLandingPage?: boolean;
  context: "remote" | "kiosk";
};

export const openQuestionnaire = ({
  token,
  context,
  queryParams,
  newTab = false,
  skipLandingPage = false,
}: OpenSurvey) => {
  let url = QUESTIONNAIRE_URL + token;
  const qp: Record<string, string> = { ...queryParams } as Record<
    string,
    string
  >;

  const accessToken = localStorage.getItem("id_token");

  if (accessToken && qp.access_token == null) {
    qp.access_token = encodeURIComponent(accessToken);
  }

  // Remove the language prompt flag so that it is not shown again once they return
  // back to the patient portal
  localStorage.removeItem("ciq_can_show_language_prompt");

  if (context === "remote") {
    localStorage.setItem("portalContext", "remote");
    localStorage.setItem("portalHomeUrl", "/complete");
  } else {
    localStorage.setItem("portalContext", "kiosk");
    localStorage.setItem("portalHomeUrl", "/institution");
  }

  // We should direct them to /0 if skipLandingPage is true
  if (skipLandingPage) {
    url = url + "/0";
  }

  Object.keys(qp || {}).forEach((key) => {
    url = updateQueryStringParameter({
      uri: url,
      key,
      value: qp[key],
    });
  });

  newTab ? window.open(url, "_blank") : window.open(url, "_self");
};

export const clearQuestionnaire = () => {
  localStorage.removeItem("sessionId");
  localStorage.removeItem("surveyCode");
  localStorage.removeItem("portalContext");
  localStorage.removeItem("portalHomeUrl");
  localStorage.removeItem("ciq-questionnaire.locale");
  localStorage.removeItem("ciq-questionnaire.sessionId");
  localStorage.removeItem("ciq-questionnaire.survey_response_jwt_token");
  localStorage.removeItem("ciq-questionnaire.lastId");
  localStorage.removeItem("ciq-questionnaire.data");
  localStorage.removeItem("ciq-questionnaire.state");
  localStorage.removeItem("ciq-questionnaire.meets_testing_criteria");
  localStorage.removeItem("ciq-questionnaire.meets_mri_criteria");
  localStorage.removeItem("ciq-questionnaire.meets_counseling_criteria");
};

export const getPatientIdentifier = () => {
  const sessionId = localStorage.getItem("ciq-questionnaire.sessionId");
  return sessionId ? sessionId.slice(sessionId.length - 4) : null;
};
